import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import { formatDateDDMMYYYY, formatDateForInput, formatDateWithAge } from '../utils/dateUtils';
import { 
  validateEmail, 
  validatePhone, 
  validateName,
  validateDateOfBirth,
  validateAge,
  validateYear,
  validateTextField,
  validateRate,
  validateFrequency,
  validateDuration,
  validateSubjectForm
} from '../utils/validationUtils';
import RateChangeConfirmModal from './RateChangeConfirmModal';
import '../assets/TuteeProfile.css?v=3';
import '../assets/TuteeProfileSubjectsRedesign.css?v=2';

const TuteeProfile = ({ readOnly = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tutee, setTutee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({});
  const [editingSubject, setEditingSubject] = useState(null);
  
  // Rate change modal state
  const [showRateChangeModal, setShowRateChangeModal] = useState(false);
  const [rateChangeData, setRateChangeData] = useState(null);
  const [unendorsedLessons, setUnendorsedLessons] = useState([]);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [availableTutors, setAvailableTutors] = useState([]);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [defaultRates, setDefaultRates] = useState({ physical: 40, online: 35 });
  const [newSubject, setNewSubject] = useState({
    name: '',
    rate: '',
    mode: 'Physical',
    frequencyPerMonth: '',
    sessionDuration: '',
    isActive: true,
    assignedTutors: []
  });
  const [subjectFieldErrors, setSubjectFieldErrors] = useState({});

  useEffect(() => {
    fetchTuteeDetails();
  }, [id]);

  const fetchTuteeDetails = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getTuteeById(id);
      setTutee(response.data);
      setFormData(response.data);
      setError('');
      // Also fetch available tutors and default rates
      await fetchAvailableTutors();
      await fetchDefaultRatesForLevel(response.data.level);
    } catch (err) {
      setError('Failed to load tutee details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTutors = async () => {
    try {
      const response = await adminApi.getTutors();
      setAvailableTutors(response.data.filter(tutor => tutor.isActive !== false));
    } catch (err) {
      console.error('Failed to fetch tutors:', err);
    }
  };

  const fetchDefaultRatesForLevel = async (level) => {
    try {
      const response = await adminApi.getTutorRateSettings();
      if (response.data && response.data.rates && level) {
        const levelRates = response.data.rates[level];
        if (levelRates) {
          setDefaultRates(levelRates);
        }
      }
    } catch (err) {
      console.error('Failed to fetch default rates:', err);
    }
  };

  const handleAddSubject = () => {
    // Clear any existing errors and reset form
    setError('');
    setNewSubject({
      name: '',
      rate: '',
      mode: 'Physical',
      frequencyPerMonth: '',
      sessionDuration: '',
      isActive: true,
      assignedTutors: []
    });
    setShowAddSubject(true);
  };

  const showSubjectFieldError = (fieldName, errorMessage) => {
    setSubjectFieldErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
    
    // Clear error after 3 seconds
    setTimeout(() => {
      setSubjectFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }, 3000);
  };

  const handleSaveNewSubject = async () => {
    // Clear any existing errors
    setError('');
    setSubjectFieldErrors({});
    
    // Manual validation with tooltips
    let hasErrors = false;
    
    // Validate subject name
    if (!newSubject.name || !newSubject.name.trim()) {
      showSubjectFieldError('name', 'Subject name is required');
      hasErrors = true;
    } else if (newSubject.name.trim().length < 2) {
      showSubjectFieldError('name', 'Subject name must be at least 2 characters');
      hasErrors = true;
    } else if (newSubject.name.trim().length > 100) {
      showSubjectFieldError('name', 'Subject name must be less than 100 characters');
      hasErrors = true;
    }
    
    // Validate rate
    if (!newSubject.rate || newSubject.rate === '') {
      showSubjectFieldError('rate', 'Hourly rate is required');
      hasErrors = true;
    } else if (parseFloat(newSubject.rate) <= 0) {
      showSubjectFieldError('rate', 'Rate must be greater than 0');
      hasErrors = true;
    } else if (parseFloat(newSubject.rate) > 1000) {
      showSubjectFieldError('rate', 'Rate must be less than $1000');
      hasErrors = true;
    }
    
    // Validate frequency (optional, but if provided must be valid)
    if (newSubject.frequencyPerMonth && newSubject.frequencyPerMonth !== '') {
      const freq = parseFloat(newSubject.frequencyPerMonth);
      if (isNaN(freq) || freq < 0) {
        showSubjectFieldError('frequencyPerMonth', 'Frequency must be a positive number');
        hasErrors = true;
      } else if (freq > 100) {
        showSubjectFieldError('frequencyPerMonth', 'Frequency seems too high (max 100)');
        hasErrors = true;
      }
    }
    
    // No validation for session duration - it's in hours and optional
    
    if (hasErrors) {
      return; // Don't close modal, just show tooltips
    }

    // Check for duplicate subject names
    const existingSubject = tutee.subjects?.find(
      subject => subject.name.toLowerCase().trim() === newSubject.name.toLowerCase().trim()
    );
    
    if (existingSubject) {
      showSubjectFieldError('name', 'A subject with this name already exists');
      return;
    }

    try {
      // Prepare the new subject with proper data types
      const subjectToAdd = {
        name: newSubject.name.trim(),
        rate: parseFloat(newSubject.rate),
        mode: newSubject.mode,
        isActive: newSubject.isActive,
        assignedTutors: []
      };
      
      // Only add frequency if it has a value
      if (newSubject.frequencyPerMonth && newSubject.frequencyPerMonth !== '') {
        subjectToAdd.frequencyPerMonth = parseFloat(newSubject.frequencyPerMonth);
      }
      
      // Only add duration if it has a value
      if (newSubject.sessionDuration && newSubject.sessionDuration !== '') {
        subjectToAdd.sessionDuration = parseFloat(newSubject.sessionDuration);
      }
      
      console.log('Adding subject with data:', subjectToAdd);
      
      const updatedSubjects = [...(tutee.subjects || []), subjectToAdd];
      const updatedTutee = { ...tutee, subjects: updatedSubjects };
      
      const response = await adminApi.updateTutee(id, updatedTutee);
      console.log('Subject added successfully:', response.data);
      setTutee(response.data);
      
      // Reset form and close modal
      setNewSubject({
        name: '',
        rate: '',
        mode: 'Physical',
        frequencyPerMonth: '',
        sessionDuration: '',
        isActive: true,
        assignedTutors: []
      });
      setShowAddSubject(false);
      setError('');
      
      setSuccess(`Subject "${subjectToAdd.name}" added successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding subject:', err);
      setError('Failed to add subject: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleEditSubject = (subjectIndex) => {
    setEditingSubject(subjectIndex);
  };

  const handleSaveSubject = async (subjectIndex, updatedSubject) => {
    try {
      // Check if tutee rate or any tutor rates have changed
      const originalSubject = tutee.subjects[subjectIndex];
      const rateChanges = [];
      
      console.log('[Rate Change Check] Original subject:', originalSubject);
      console.log('[Rate Change Check] Updated subject:', updatedSubject);
      
      // Check if tutee rate changed
      if (originalSubject.rate !== updatedSubject.rate) {
        console.log('[Tutee Rate Change] Detected:', {
          oldRate: originalSubject.rate,
          newRate: updatedSubject.rate
        });
        
        // For tutee rate changes, we need to update ALL lessons for this subject
        // regardless of tutor, so we'll handle this separately
        // For now, we'll add a flag to indicate tutee rate changed
        rateChanges.push({
          type: 'tuteeRate',
          oldRate: originalSubject.rate,
          newRate: updatedSubject.rate,
          subjectIndex,
          subject: updatedSubject.name
        });
      }
      
      // Check if any tutor rates have changed
      if (updatedSubject.assignedTutors && originalSubject.assignedTutors) {
        for (let i = 0; i < updatedSubject.assignedTutors.length; i++) {
          const updatedAssignment = updatedSubject.assignedTutors[i];
          const originalAssignment = originalSubject.assignedTutors.find(
            a => (a.tutor?._id || a.tutor) === (updatedAssignment.tutor?._id || updatedAssignment.tutor)
          );
          
          if (originalAssignment) {
            const oldRate = originalAssignment.tutorRate;
            const newRate = updatedAssignment.tutorRate;
            
            console.log('[Tutor Rate Change Check] Comparing rates:', {
              tutorId: updatedAssignment.tutor?._id || updatedAssignment.tutor,
              oldRate,
              newRate,
              changed: oldRate !== newRate
            });
            
            // Check if rate actually changed (handle null/undefined/empty string)
            const oldRateValue = oldRate === null || oldRate === undefined || oldRate === '' ? null : parseFloat(oldRate);
            const newRateValue = newRate === null || newRate === undefined || newRate === '' ? null : parseFloat(newRate);
            
            if (oldRateValue !== newRateValue) {
              const tutorId = updatedAssignment.tutor?._id || updatedAssignment.tutor;
              const tutorName = updatedAssignment.tutor?.name || 
                               originalAssignment.tutor?.name || 
                               'Unknown Tutor';
              
              console.log('[Tutor Rate Change] Detected!', {
                tutorId,
                tutorName,
                oldRate: oldRateValue,
                newRate: newRateValue
              });
              
              rateChanges.push({
                type: 'tutorRate',
                tutorId,
                tutorName,
                oldRate: oldRateValue,
                newRate: newRateValue,
                subjectIndex,
                subject: updatedSubject.name
              });
            }
          }
        }
      }
      
      console.log('[Rate Change Check] Total rate changes found:', rateChanges.length);
      
      // If there are rate changes, check for unendorsed lessons
      if (rateChanges.length > 0) {
        // If both tutee and tutor rates changed, prioritize tutee rate first
        // (tutee rate affects all tutors, so it's more critical)
        const rateChange = rateChanges.find(rc => rc.type === 'tuteeRate') || rateChanges[0];
        
        // Store all rate changes for later processing
        if (rateChanges.length > 1) {
          console.log('[Rate Change Check] Multiple rate changes detected. Handling:', rateChange.type, 'first');
          // Store remaining changes to handle after first one is confirmed
          const remainingChanges = rateChanges.filter(rc => rc !== rateChange);
          setPendingSaveData({ 
            subjectIndex, 
            updatedSubject, 
            remainingRateChanges: remainingChanges 
          });
        } else {
          setPendingSaveData({ subjectIndex, updatedSubject });
        }
        
        console.log('[Rate Change Check] Checking unendorsed lessons for:', rateChange);
        
        try {
          // For tutee rate changes, we need to check ALL tutors for this subject
          // For tutor rate changes, check only the specific tutor
          let tutorId;
          
          if (rateChange.type === 'tutorRate') {
            tutorId = rateChange.tutorId;
          } else {
            // For tutee rate changes, use the first tutor (API will return all lessons for subject)
            tutorId = updatedSubject.assignedTutors[0]?.tutor?._id || updatedSubject.assignedTutors[0]?.tutor;
          }
          
          if (!tutorId) {
            console.error('[Rate Change Check] No tutor ID available');
            // Continue with normal save if no tutor assigned
            await performSave(subjectIndex, updatedSubject);
            return;
          }
          
          const response = await adminApi.checkUnendorsedLessons(
            id,
            subjectIndex,
            tutorId
          );
          
          console.log('[Rate Change Check] Unendorsed lessons response:', response.data);
          
          // Store the data for later use
          setRateChangeData(rateChange);
          setUnendorsedLessons(response.data.lessons || []);
          setShowRateChangeModal(true);
          return; // Don't save yet, wait for modal confirmation
        } catch (err) {
          console.error('[Rate Change Check] Error checking unendorsed lessons:', err);
          // If check fails, continue with normal save
        }
      }
      
      // No rate changes or check failed, proceed with normal save
      console.log('[Rate Change Check] No rate changes detected, proceeding with normal save');
      await performSave(subjectIndex, updatedSubject);
    } catch (err) {
      console.error('Error in handleSaveSubject:', err);
      const errorMessage = err.response?.data?.message || err.message;
      setError('Failed to update subject: ' + errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };
  
  const performSave = async (subjectIndex, updatedSubject) => {
    try {
      // Ensure tutor information is properly formatted for the backend
      if (updatedSubject.assignedTutors) {
        updatedSubject.assignedTutors = updatedSubject.assignedTutors.map(assignment => {
          // Extract just the ObjectId for the tutor field
          const tutorId = assignment.tutor?._id || assignment.tutorId || assignment.tutor;
          
          // Skip assignments with no valid tutor ID
          if (!tutorId) {
            return null;
          }
          
          // Filter out incomplete schedules (must have both day and startTime)
          const validSchedules = (assignment.schedules || []).filter(
            schedule => schedule.day && schedule.startTime
          );
          
          // Return properly formatted assignment
          return {
            tutor: tutorId, // Backend expects just the ObjectId
            tutorRate: assignment.tutorRate || null, // Keep custom rate or null for default
            assignedAt: assignment.assignedAt || assignment.assignedDate || new Date(),
            schedule: assignment.schedule || null,
            schedules: validSchedules
          };
        }).filter(a => a !== null); // Filter out null assignments
      }
      
      const updatedSubjects = [...tutee.subjects];
      updatedSubjects[subjectIndex] = updatedSubject;
      const updatedTutee = { ...tutee, subjects: updatedSubjects };
      
      console.log('Saving tutee with schedules:', JSON.stringify(updatedTutee.subjects[subjectIndex].assignedTutors, null, 2));
      
      const response = await adminApi.updateTutee(id, updatedTutee);
      setTutee(response.data);
      setEditingSubject(null);
      setSuccess('Subject updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating subject:', err);
      const errorMessage = err.response?.data?.message || err.message;
      setError('Failed to update subject: ' + errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };
  
  const handleRateChangeConfirm = async (shouldUpdateLessons) => {
    try {
      if (shouldUpdateLessons && unendorsedLessons.length > 0 && rateChangeData) {
        // Update the lesson rates
        const lessonIds = unendorsedLessons.map(l => l._id);
        
        // For tutee rate changes, tutorId might not be set (will update all tutors)
        // For tutor rate changes, tutorId is required
        const tutorId = rateChangeData.tutorId || null;
        
        console.log('[Rate Change Confirm] Updating lessons with:');
        console.log('  - Lesson IDs:', lessonIds);
        console.log('  - New Rate:', rateChangeData.newRate);
        console.log('  - Rate Type:', rateChangeData.type);
        console.log('  - Tutor ID:', tutorId);
        console.log('  - Tutee ID:', id);
        console.log('  - Subject:', rateChangeData.subject);
        
        await adminApi.bulkUpdateLessonRates(
          lessonIds,
          rateChangeData.newRate,
          tutorId,
          id,
          rateChangeData.subject,
          rateChangeData.type // Pass the rate type (tuteeRate or tutorRate)
        );
        
        setSuccess(`Updated ${lessonIds.length} lesson(s) with new ${rateChangeData.type === 'tuteeRate' ? 'tutee' : 'tutor'} rate`);
      }
      
      // Check if there are remaining rate changes to process
      if (pendingSaveData?.remainingRateChanges && pendingSaveData.remainingRateChanges.length > 0) {
        const nextRateChange = pendingSaveData.remainingRateChanges[0];
        const remainingChanges = pendingSaveData.remainingRateChanges.slice(1);
        
        console.log('[Rate Change Confirm] Processing next rate change:', nextRateChange);
        
        try {
          // Get tutor ID for the next rate change
          let tutorId;
          if (nextRateChange.type === 'tutorRate') {
            tutorId = nextRateChange.tutorId;
          } else {
            const updatedSubject = pendingSaveData.updatedSubject;
            tutorId = updatedSubject.assignedTutors[0]?.tutor?._id || updatedSubject.assignedTutors[0]?.tutor;
          }
          
          if (tutorId) {
            const response = await adminApi.checkUnendorsedLessons(
              id,
              pendingSaveData.subjectIndex,
              tutorId
            );
            
            // Update state for next rate change
            setRateChangeData(nextRateChange);
            setUnendorsedLessons(response.data.lessons || []);
            setPendingSaveData({
              ...pendingSaveData,
              remainingRateChanges: remainingChanges
            });
            setShowRateChangeModal(true);
            return; // Don't continue, wait for next confirmation
          }
        } catch (err) {
          console.error('[Rate Change Confirm] Error checking next rate change:', err);
          // Continue with save if error
        }
      }
      
      // No more rate changes, continue with normal save
      if (pendingSaveData) {
        await performSave(pendingSaveData.subjectIndex, pendingSaveData.updatedSubject);
      }
      
      // Clean up
      setShowRateChangeModal(false);
      setRateChangeData(null);
      setUnendorsedLessons([]);
      setPendingSaveData(null);
    } catch (err) {
      console.error('Error in rate change confirmation:', err);
      setError('Failed to update lesson rates: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 5000);
      
      // Clean up even on error
      setShowRateChangeModal(false);
      setRateChangeData(null);
      setUnendorsedLessons([]);
      setPendingSaveData(null);
    }
  };

  const handleDeleteSubject = async (subjectIndex) => {
    if (window.confirm('Are you sure you want to delete this subject?')) {
      try {
        const updatedSubjects = tutee.subjects.filter((_, index) => index !== subjectIndex);
        const updatedTutee = { ...tutee, subjects: updatedSubjects };
        
        const response = await adminApi.updateTutee(id, updatedTutee);
        setTutee(response.data);
        setSuccess('Subject deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Failed to delete subject: ' + (err.response?.data?.message || err.message));
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleAssignTutor = async (subjectIndex, tutorId, tutorRate) => {
    try {
      const tutor = availableTutors.find(t => t._id === tutorId);
      if (!tutor) {
        setError('Selected tutor not found');
        setTimeout(() => setError(''), 3000);
        return;
      }

      const updatedSubjects = [...tutee.subjects];
      const subject = updatedSubjects[subjectIndex];
      
      // Check if tutor is already assigned
      const existingAssignment = subject.assignedTutors?.find(
        assignment => (assignment.tutor?._id || assignment.tutorId) === tutorId
      );
      
      if (existingAssignment) {
        setError('Tutor is already assigned to this subject');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // Get default rate if no rate provided
      let finalRate = tutorRate;
      if (!finalRate) {
        try {
          const rateResponse = await adminApi.getTutorRateSettings();
          if (rateResponse.data && rateResponse.data.rates && tutee.level) {
            const levelRates = rateResponse.data.rates[tutee.level];
            if (levelRates) {
              const mode = (subject.mode || 'physical').toLowerCase();
              finalRate = levelRates[mode] || levelRates.physical || subject.rate;
            }
          }
        } catch (err) {
          console.error('Failed to fetch default rates:', err);
          finalRate = subject.rate; // Fallback to subject rate
        }
      }

      const newAssignment = {
        tutor: tutor,
        tutorId: tutorId,
        name: tutor.name,
        tutorRate: tutorRate || null, // Only store custom rate, null for default
        assignedDate: new Date(),
        isUsingDefault: !tutorRate, // Flag to indicate if using default rate
        schedules: [] // Initialize empty schedules array
      };

      if (!subject.assignedTutors) {
        subject.assignedTutors = [];
      }
      subject.assignedTutors.push(newAssignment);
      
      const updatedTutee = { ...tutee, subjects: updatedSubjects };
      const response = await adminApi.updateTutee(id, updatedTutee);
      setTutee(response.data);
      
      const rateMessage = tutorRate ? `at $${tutorRate}/hr` : `at default rate $${finalRate}/hr`;
      setSuccess(`Tutor ${tutor.name} assigned successfully ${rateMessage}!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to assign tutor: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleRemoveTutor = async (subjectIndex, tutorIndex) => {
    try {
      const updatedSubjects = [...tutee.subjects];
      updatedSubjects[subjectIndex].assignedTutors.splice(tutorIndex, 1);
      
      const updatedTutee = { ...tutee, subjects: updatedSubjects };
      const response = await adminApi.updateTutee(id, updatedTutee);
      setTutee(response.data);
      setSuccess('Tutor removed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to remove tutor: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 3000);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validation based on field type
    let validatedValue = value;
    
    if (name === 'phone' || name === 'parent.phone') {
      // Phone: numbers, +, spaces, and hyphens only
      validatedValue = value.replace(/[^0-9+\s\-]/g, '');
    } else if (name === 'race') {
      // Race: letters, spaces, and common punctuation only
      validatedValue = value.replace(/[^a-zA-Z\s\-']/g, '');
    }
    
    if (name.includes('.')) {
      const parts = name.split('.');
      setFormData(prev => {
        const updated = { ...prev };
        let current = updated;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current[parts[i]] = { ...current[parts[i]] };
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = validatedValue;
        return updated;
      });
    } else {
      setFormData(prev => {
        const updated = { ...prev, [name]: validatedValue };
        
        // If dateOfBirth is changed, calculate and update age
        if (name === 'dateOfBirth') {
          updated.age = calculateAge(validatedValue);
        }
        
        return updated;
      });
    }
  };

  const startEditing = (section) => {
    setEditingSection(section);
    // Ensure parent object exists in formData
    const initialFormData = { ...tutee };
    if (section === 'parent' && !initialFormData.parent) {
      initialFormData.parent = {};
    }
    setFormData(initialFormData);
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setFormData(tutee);
  };

  // Validation state
  const [fieldErrors, setFieldErrors] = useState({});

  const validateSection = (sectionName) => {
    const errors = {};
    
    if (sectionName === 'Personal Information') {
      // Validate name
      if (formData.name) {
        const nameValidation = validateName(formData.name);
        if (!nameValidation.isValid) errors.name = nameValidation.error;
      }
      
      // Validate email
      if (formData.email) {
        const emailValidation = validateEmail(formData.email);
        if (!emailValidation.isValid) errors.email = emailValidation.error;
      }
      
      // Validate phone
      if (formData.phone) {
        const phoneValidation = validatePhone(formData.phone);
        if (!phoneValidation.isValid) errors.phone = phoneValidation.error;
      }
      
      // Validate year
      if (formData.year) {
        const yearValidation = validateYear(formData.year);
        if (!yearValidation.isValid) errors.year = yearValidation.error;
      }
      
      // Validate date of birth
      if (formData.dateOfBirth) {
        const dobValidation = validateDateOfBirth(formData.dateOfBirth);
        if (!dobValidation.isValid) errors.dateOfBirth = dobValidation.error;
      }
      
      // Validate age
      if (formData.age) {
        const ageValidation = validateAge(formData.age);
        if (!ageValidation.isValid) errors.age = ageValidation.error;
      }
      
      // Validate school
      if (formData.school) {
        const schoolValidation = validateTextField(formData.school, 200, 'School');
        if (!schoolValidation.isValid) errors.school = schoolValidation.error;
      }
      
      // Validate notes
      if (formData.notes) {
        const notesValidation = validateTextField(formData.notes, 2000, 'Notes');
        if (!notesValidation.isValid) errors.notes = notesValidation.error;
      }
    }
    
    if (sectionName === 'Parent Information') {
      // Validate parent name
      if (formData.parent?.name) {
        const nameValidation = validateName(formData.parent.name);
        if (!nameValidation.isValid) errors.parentName = nameValidation.error;
      }
      
      // Validate parent email
      if (formData.parent?.email) {
        const emailValidation = validateEmail(formData.parent.email);
        if (!emailValidation.isValid) errors.parentEmail = emailValidation.error;
      }
      
      // Validate parent phone
      if (formData.parent?.phone) {
        const phoneValidation = validatePhone(formData.parent.phone);
        if (!phoneValidation.isValid) errors.parentPhone = phoneValidation.error;
      }
    }
    
    return errors;
  };

  const showFieldError = (fieldName, errorMessage) => {
    setFieldErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
    
    // Clear error after 5 seconds
    setTimeout(() => {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }, 5000);
  };

  const saveSection = async (sectionName) => {
    try {
      // Validate before saving
      const errors = validateSection(sectionName);
      
      if (Object.keys(errors).length > 0) {
        // Show all errors
        setFieldErrors(errors);
        setError('Please fix the validation errors before saving');
        setTimeout(() => setError(''), 3000);
        
        // Clear errors after 5 seconds
        setTimeout(() => setFieldErrors({}), 5000);
        return;
      }
      
      // Clean the data before sending - ensure subjects have proper tutor format
      const cleanedData = { ...formData };
      if (cleanedData.subjects) {
        cleanedData.subjects = cleanedData.subjects.map(subject => ({
          ...subject,
          assignedTutors: (subject.assignedTutors?.map(assignment => {
            const tutorId = assignment.tutor?._id || assignment.tutorId || assignment.tutor;
            
            // Skip assignments with no valid tutor ID
            if (!tutorId) {
              return null;
            }
            
            return {
              tutor: tutorId,
              tutorRate: assignment.tutorRate || null,
              assignedAt: assignment.assignedAt || assignment.assignedDate || new Date(),
              schedule: assignment.schedule || null,
              schedules: assignment.schedules || []
            };
          }).filter(a => a !== null)) || [] // Filter out null assignments
        }));
      }
      
      const response = await adminApi.updateTutee(id, cleanedData);
      setTutee(response.data);
      setFormData(response.data);
      setEditingSection(null);
      setFieldErrors({});
      setSuccess(`${sectionName} updated successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('TuteeProfile - Save error:', err);
      setError(`Failed to update ${sectionName.toLowerCase()}: ` + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleArchive = async () => {
    // Use tutee._id instead of id from params (same as AdminTutees)
    const tuteeId = tutee?._id || id;
    
    if (window.confirm(`Archive ${tutee.name}? They will be marked as inactive but data will be preserved.`)) {
      try {
        const response = await adminApi.deleteTutee(tuteeId);
        
        setSuccess('Tutee archived successfully!');
        setTimeout(() => setSuccess(''), 3000);
        await fetchTuteeDetails();
      } catch (err) {
        console.error('[FRONTEND] Archive error:', err);
        console.error('[FRONTEND] Error response:', err.response);
        console.error('[FRONTEND] Error data:', err.response?.data);
        
        const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error occurred';
        setError('Failed to archive tutee: ' + errorMessage);
        setTimeout(() => setError(''), 5000);
      }
    }
  };

  const handleUnarchive = async () => {
    if (window.confirm(`Reactivate ${tutee.name}?`)) {
      try {
        await adminApi.bulkUnarchiveTutees([id]);
        setSuccess('Tutee reactivated successfully!');
        setTimeout(() => setSuccess(''), 3000);
        await fetchTuteeDetails();
      } catch (err) {
        setError('Failed to reactivate tutee: ' + (err.response?.data?.message || err.message));
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handlePermanentDelete = async () => {
    if (window.confirm(`⚠️ PERMANENT DELETE: Are you sure you want to permanently delete ${tutee.name}? This action CANNOT be undone and will remove all data including lessons, subjects, and history.`)) {
      if (window.confirm(`Final confirmation: Type the tutee's name to confirm deletion.\n\nThis will permanently delete: ${tutee.name}`)) {
        try {
          await adminApi.permanentlyDeleteTutee(id);
          setSuccess('Tutee permanently deleted.');
          setTimeout(() => {
            navigate('/admin/tutees');
          }, 1500);
        } catch (err) {
          setError('Failed to delete tutee: ' + (err.response?.data?.message || err.message));
          setTimeout(() => setError(''), 3000);
        }
      }
    }
  };

  if (loading) return <div className="loading">Loading tutee details...</div>;
  if (error && !tutee) return <div className="error">{error}</div>;
  if (!tutee) return <div className="error">Tutee not found</div>;

  return (
    <div className={`tutor-profile tutee-profile ${readOnly ? 'read-only-profile' : ''}`}>
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <button className="btn-back-modern" onClick={() => navigate(-1)}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M12 4L6 10l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Tutees
      </button>

      <div className="profile-header">
        <div className="profile-header-main">
          <div className="profile-avatar">
            {tutee.name.charAt(0).toUpperCase()}
          </div>
          <div className="profile-header-info">
            <h1>{tutee.name}</h1>
            <p className="profile-email">{tutee.email || 'No email provided'}</p>
            <div className="profile-badges">
              <span className={`status-badge-new ${tutee.isActive !== false ? 'active' : 'inactive'}`}>
                <span className="badge-dot"></span>
                {tutee.isActive !== false ? 'Active' : 'Inactive'}
              </span>
              <span className="role-badge-new">Tutee</span>
              {tutee.code && (
                <span className="code-badge-new">{tutee.code}</span>
              )}
            </div>
            {(tutee.createdAt || tutee.updatedAt) && (
              <div className="profile-timestamps-footer">
                {tutee.createdAt && (
                  <span>Created {new Date(tutee.createdAt).toLocaleString('en-SG', { 
                    timeZone: 'Asia/Singapore',
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                )}
                {tutee.createdAt && tutee.updatedAt && <span className="timestamp-separator">•</span>}
                {tutee.updatedAt && (
                  <span>Last modified {new Date(tutee.updatedAt).toLocaleString('en-SG', { 
                    timeZone: 'Asia/Singapore',
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="profile-actions-redesigned">
          {tutee.isActive !== false ? (
            <button 
              className="action-button archive-style"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleArchive();
              }}
              title="Archive Tutee"
              type="button"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 4h12M3 4v9a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4M5 4V2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Archive
            </button>
          ) : (
            <>
              <button 
                className="action-button reactivate-style"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleUnarchive();
                }}
                title="Reactivate Tutee"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2v4M8 10v4M2 8h4M10 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Reactivate
              </button>
              <button 
                className="action-button delete-style"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePermanentDelete();
                }}
                title="Permanently Delete Tutee"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 4h12M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1M13 4v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4h10zM6.5 7v4M9.5 7v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      <div className="profile-content">
        <div className={`profile-section editable-section ${editingSection === 'personal' ? 'editing' : ''}`}>
          <button 
            className="btn-edit-corner" 
            onClick={() => editingSection === 'personal' ? cancelEditing() : startEditing('personal')}
            title={editingSection === 'personal' ? "Cancel" : "Edit"}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              {editingSection === 'personal' ? (
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M10 1.5a1.5 1.5 0 0 1 2.1 2.1L4.5 11.1l-3 .4.4-3L10 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              )}
            </svg>
          </button>
          
          <h2>Personal Information</h2>
          {editingSection === 'personal' ? (
            <>
              <div className="info-grid">
                <div className={`info-item ${fieldErrors.phone ? 'has-error' : ''}`}>
                  <label>Phone</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    value={formData.phone || ''} 
                    onChange={handleInputChange}
                    placeholder="e.g., +65 9123 4567"
                  />
                  {fieldErrors.phone && (
                    <>
                      <span className="field-error-icon">⚠</span>
                      <div className="field-error-tooltip">{fieldErrors.phone}</div>
                    </>
                  )}
                </div>
                <div className={`info-item ${fieldErrors.email ? 'has-error' : ''}`}>
                  <label>Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email || ''} 
                    onChange={handleInputChange}
                    placeholder="e.g., student@example.com"
                  />
                  {fieldErrors.email && (
                    <>
                      <span className="field-error-icon">⚠</span>
                      <div className="field-error-tooltip">{fieldErrors.email}</div>
                    </>
                  )}
                </div>
                <div className={`info-item ${fieldErrors.dateOfBirth ? 'has-error' : ''}`}>
                  <label>Date of Birth (Age)</label>
                  <input 
                    type="date" 
                    name="dateOfBirth" 
                    value={formData.dateOfBirth ? formData.dateOfBirth.split('T')[0] : ''} 
                    onChange={handleInputChange}
                  />
                  {fieldErrors.dateOfBirth && (
                    <>
                      <span className="field-error-icon">⚠</span>
                      <div className="field-error-tooltip">{fieldErrors.dateOfBirth}</div>
                    </>
                  )}
                </div>
                <div className="info-item">
                  <label>Gender</label>
                  <select name="gender" value={formData.gender || ''} onChange={handleInputChange}>
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="info-item">
                  <label>Race</label>
                  <input 
                    type="text" 
                    name="race" 
                    value={formData.race || ''} 
                    onChange={handleInputChange}
                    placeholder="e.g., Chinese, Malay, Indian"
                  />
                </div>
                <div className="info-item">
                  <label>Address</label>
                  <input type="text" name="address" value={formData.address || ''} onChange={handleInputChange} placeholder="e.g., 123 Main Street, Singapore 123456" />
                </div>
              </div>
              
              <h4 style={{ marginTop: '20px', marginBottom: '15px', color: 'var(--gray-700)', fontSize: '1rem' }}>Education Information</h4>
              <div className="info-grid">
                <div className="info-item">
                  <label>School</label>
                  <input type="text" name="school" value={formData.school || ''} onChange={handleInputChange} />
                </div>
                <div className={`info-item ${fieldErrors.year ? 'has-error' : ''}`}>
                  <label>Year</label>
                  <input 
                    type="number" 
                    name="year" 
                    value={formData.year || ''} 
                    onChange={handleInputChange} 
                    placeholder="e.g., 2025"
                  />
                  {fieldErrors.year && (
                    <>
                      <span className="field-error-icon">⚠</span>
                      <div className="field-error-tooltip">{fieldErrors.year}</div>
                    </>
                  )}
                </div>
                <div className="info-item">
                  <label>Level</label>
                  <select name="level" value={formData.level || ''} onChange={handleInputChange}>
                    <option value="">Select level</option>
                    <option value="Kindergarten 1">Kindergarten 1</option>
                    <option value="Kindergarten 2">Kindergarten 2</option>
                    <option value="Primary 1">Primary 1</option>
                    <option value="Primary 2">Primary 2</option>
                    <option value="Primary 3">Primary 3</option>
                    <option value="Primary 4">Primary 4</option>
                    <option value="Primary 5">Primary 5</option>
                    <option value="Primary 6">Primary 6</option>
                    <option value="Secondary 1">Secondary 1</option>
                    <option value="Secondary 2">Secondary 2</option>
                    <option value="Secondary 3">Secondary 3</option>
                    <option value="Secondary 4">Secondary 4</option>
                    <option value="Secondary 5">Secondary 5</option>
                    <option value="JC 1">JC 1</option>
                    <option value="JC 2">JC 2</option>
                    <option value="Polytechnic Year 1">Polytechnic Year 1</option>
                    <option value="Polytechnic Year 2">Polytechnic Year 2</option>
                    <option value="Polytechnic Year 3">Polytechnic Year 3</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
              </div>
              <div className="section-actions">
                <button className="btn-save-section" onClick={() => saveSection('Personal Information')}>Save</button>
              </div>
            </>
          ) : (
            <>
              <div className="info-grid">
                <div className="info-item">
                  <label>Phone</label>
                  <p>{tutee.phone || 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Email</label>
                  <p>{tutee.email || 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Date of Birth (Age)</label>
                  <p>{tutee.dateOfBirth ? `${new Date(tutee.dateOfBirth).toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore' })} (${calculateAge(tutee.dateOfBirth)} years)` : tutee.age ? `Age: ${tutee.age}` : 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Gender</label>
                  <p>{tutee.gender || 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Race</label>
                  <p>{tutee.race || 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Address</label>
                  <p>{tutee.address || 'N/A'}</p>
                </div>
              </div>
              
              <h4 style={{ marginTop: '20px', marginBottom: '15px', color: 'var(--gray-700)', fontSize: '1rem' }}>Education Information</h4>
              <div className="info-grid">
                <div className="info-item">
                  <label>School</label>
                  <p>{tutee.school || 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Year</label>
                  <p>{tutee.year || 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Level</label>
                  <p>{tutee.level || 'N/A'}</p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={`profile-section editable-section ${editingSection === 'parent' ? 'editing' : ''}`}>
          <button 
            className="btn-edit-corner" 
            onClick={() => editingSection === 'parent' ? cancelEditing() : startEditing('parent')}
            title={editingSection === 'parent' ? "Cancel" : "Edit"}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              {editingSection === 'parent' ? (
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M10 1.5a1.5 1.5 0 0 1 2.1 2.1L4.5 11.1l-3 .4.4-3L10 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              )}
            </svg>
          </button>
          
          <h2>Parent Information</h2>
          {editingSection === 'parent' ? (
            <>
              <div className="info-grid">
                <div className="info-item">
                  <label>Parent Name</label>
                  <input type="text" name="parent.name" value={formData.parent?.name || ''} onChange={handleInputChange} />
                </div>
                <div className={`info-item ${fieldErrors.parentPhone ? 'has-error' : ''}`}>
                  <label>Parent Phone</label>
                  <input 
                    type="tel" 
                    name="parent.phone" 
                    value={formData.parent?.phone || ''} 
                    onChange={handleInputChange}
                    placeholder="e.g., +65 9123 4567"
                  />
                  {fieldErrors.parentPhone && (
                    <>
                      <span className="field-error-icon">⚠</span>
                      <div className="field-error-tooltip">{fieldErrors.parentPhone}</div>
                    </>
                  )}
                </div>
                <div className={`info-item ${fieldErrors.parentEmail ? 'has-error' : ''}`}>
                  <label>Parent Email</label>
                  <input 
                    type="email" 
                    name="parent.email" 
                    value={formData.parent?.email || ''} 
                    onChange={handleInputChange}
                    placeholder="e.g., parent@example.com"
                  />
                  {fieldErrors.parentEmail && (
                    <>
                      <span className="field-error-icon">⚠</span>
                      <div className="field-error-tooltip">{fieldErrors.parentEmail}</div>
                    </>
                  )}
                </div>
                <div className="info-item">
                  <label>Relationship</label>
                  <input 
                    type="text" 
                    name="parent.relationship" 
                    value={formData.parent?.relationship || ''} 
                    onChange={handleInputChange}
                    placeholder="e.g., Father, Mother, Guardian"
                  />
                </div>
              </div>
              <div className="section-actions">
                <button className="btn-save-section" onClick={() => saveSection('Parent Information')}>Save</button>
              </div>
            </>
          ) : (
            <div className="info-grid">
              <div className="info-item">
                <label>Parent Name</label>
                <p>{tutee.parent?.name || 'N/A'}</p>
              </div>
              <div className="info-item">
                <label>Parent Phone</label>
                <p>{tutee.parent?.phone || 'N/A'}</p>
              </div>
              <div className="info-item">
                <label>Parent Email</label>
                <p>{tutee.parent?.email || 'N/A'}</p>
              </div>
              <div className="info-item">
                <label>Relationship</label>
                <p>{tutee.parent?.relationship || 'N/A'}</p>
              </div>
            </div>
          )}














        </div>

        <div className={`profile-section editable-section subjects-section ${editingSection === 'subjects' ? 'editing' : ''}`}>
          <button 
            className="btn-edit-corner" 
            onClick={handleAddSubject}
            title="Add New Subject"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
          
          <h2>Subjects & Tutor Assignments</h2>
          {/* Default Rates Display */}
          {tutee.level && (
            <DefaultRatesDisplay tuteeLevel={tutee.level} />
          )}
          
          {/* Add Subject Modal */}
          {showAddSubject && (
            <div className="add-subject-modal-overlay" onClick={() => setShowAddSubject(false)}>
              <div className="add-subject-modal" onClick={(e) => e.stopPropagation()}>
                <div className="add-subject-header">
                  <div className="modal-title-section">
                    <div className="modal-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <h3>Add New Subject</h3>
                      <p>Create a new subject for {tutee.name}</p>
                    </div>
                  </div>
                  <button className="modal-close-enhanced" onClick={() => setShowAddSubject(false)}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
                
                <div className="add-subject-form">
                  <div className="form-section">
                    <h4>Subject Details</h4>
                    <div className="form-row-two-col">
                      <div className={`form-field ${subjectFieldErrors.name ? 'has-error' : ''}`}>
                        <label>Subject Name *</label>
                        <div className="input-wrapper">
                          <input
                            type="text"
                            value={newSubject.name}
                            onChange={(e) => {
                              setNewSubject({...newSubject, name: e.target.value});
                              // Clear error when user starts typing
                              if (subjectFieldErrors.name) {
                                setSubjectFieldErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.name;
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="e.g., Mathematics, English, Science"
                          />
                          <div className="input-icon">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M2 3h12M2 7h12M2 11h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </div>
                          {subjectFieldErrors.name && (
                            <div className="field-error-tooltip fade-in">{subjectFieldErrors.name}</div>
                          )}
                        </div>
                      </div>
                      
                      <div className={`form-field ${subjectFieldErrors.rate ? 'has-error' : ''}`}>
                        <label>Hourly Rate *</label>
                        <div className="input-wrapper">
                          <div className="currency-input">
                            <span className="currency-symbol">$</span>
                            <input
                              type="number"
                              value={newSubject.rate}
                              onChange={(e) => {
                                setNewSubject({...newSubject, rate: e.target.value});
                                // Clear error when user starts typing
                                if (subjectFieldErrors.rate) {
                                  setSubjectFieldErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors.rate;
                                    return newErrors;
                                  });
                                }
                              }}
                              placeholder=""
                              min="0"
                              step="0.01"
                            />
                            <span className="currency-unit">/hr</span>
                          </div>
                          {subjectFieldErrors.rate && (
                            <div className="field-error-tooltip fade-in">{subjectFieldErrors.rate}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-row-two-col">
                      <div className={`form-field ${subjectFieldErrors.frequencyPerMonth ? 'has-error' : ''}`}>
                        <label>Frequency per Month</label>
                        <div className="input-wrapper">
                          <input
                            type="number"
                            value={newSubject.frequencyPerMonth}
                            onChange={(e) => {
                              setNewSubject({...newSubject, frequencyPerMonth: e.target.value});
                              // Clear error when user starts typing
                              if (subjectFieldErrors.frequencyPerMonth) {
                                setSubjectFieldErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.frequencyPerMonth;
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder=""
                            min="0"
                            step="any"
                          />
                          <div className="input-icon">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <rect x="2" y="3" width="12" height="11" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M2 6h12M5 1v4M11 1v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </div>
                          {subjectFieldErrors.frequencyPerMonth && (
                            <div className="field-error-tooltip fade-in">{subjectFieldErrors.frequencyPerMonth}</div>
                          )}
                        </div>
                        <small style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>
                          Expected number of sessions per month
                        </small>
                      </div>
                      
                      <div className="form-field">
                        <label>Session Duration (hours)</label>
                        <div className="input-wrapper">
                          <input
                            type="number"
                            value={newSubject.sessionDuration}
                            onChange={(e) => setNewSubject({...newSubject, sessionDuration: e.target.value})}
                            placeholder=""
                            min="0"
                            step="any"
                          />
                          <div className="input-icon">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M8 4v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </div>
                        </div>
                        <small style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>
                          Typical duration per session
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h4>Lesson Settings</h4>
                    <div className="form-row-two-col">
                      <div className="form-field">
                        <label>Lesson Mode</label>
                        <div className="mode-selector">
                          <div 
                            className={`mode-option ${newSubject.mode === 'Physical' ? 'selected' : ''}`}
                            onClick={() => setNewSubject({...newSubject, mode: 'Physical'})}
                          >
                            <div className="mode-icon">
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M10 2L4 5v10l6 3 6-3V5l-6-3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div className="mode-text">
                              <span className="mode-title">Physical</span>
                              <span className="mode-desc">In-person lessons</span>
                            </div>
                          </div>
                          
                          <div 
                            className={`mode-option ${newSubject.mode === 'Online' ? 'selected' : ''}`}
                            onClick={() => setNewSubject({...newSubject, mode: 'Online'})}
                          >
                            <div className="mode-icon">
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M2 6h16M2 10h16M2 14h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </div>
                            <div className="mode-text">
                              <span className="mode-title">Online</span>
                              <span className="mode-desc">Virtual lessons</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-field">
                        <label>Status</label>
                        <div className="status-toggle">
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={newSubject.isActive}
                              onChange={(e) => setNewSubject({...newSubject, isActive: e.target.checked})}
                            />
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">
                              {newSubject.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-actions-enhanced">
                    <button 
                      className="btn-cancel-enhanced" 
                      onClick={() => {
                        setShowAddSubject(false);
                        setError('');
                        setSubjectFieldErrors({});
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      Cancel
                    </button>
                    <button 
                      className="btn-submit-enhanced"
                      onClick={handleSaveNewSubject}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13 4L6 11l-3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Add Subject
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="subjects-grid-container">
            {tutee.subjects && tutee.subjects.length > 0 ? (
              tutee.subjects.map((subject, index) => (
                <SubjectCard
                  key={index}
                  subject={subject}
                  index={index}
                  isEditing={editingSubject === index}
                  availableTutors={availableTutors}
                  tuteeLevel={tutee.level}
                  defaultRates={defaultRates}
                  onEdit={() => handleEditSubject(index)}
                  onSave={(updatedSubject) => handleSaveSubject(index, updatedSubject)}
                  onDelete={() => handleDeleteSubject(index)}
                  onAssignTutor={(tutorId, tutorRate) => handleAssignTutor(index, tutorId, tutorRate)}
                  onRemoveTutor={(tutorIndex) => handleRemoveTutor(index, tutorIndex)}
                  onCancel={() => setEditingSubject(null)}
                />
              ))
            ) : (
              <div className="no-subjects-placeholder">
                <h3>No Subjects Assigned</h3>
                <p>This tutee doesn't have any subjects assigned yet.</p>
              </div>
            )}
          </div>
        </div>

        <div className={`profile-section editable-section ${editingSection === 'notes' ? 'editing' : ''}`}>
          <button 
            className="btn-edit-corner" 
            onClick={() => editingSection === 'notes' ? cancelEditing() : startEditing('notes')}
            title={editingSection === 'notes' ? "Cancel" : "Edit"}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              {editingSection === 'notes' ? (
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M10 1.5a1.5 1.5 0 0 1 2.1 2.1L4.5 11.1l-3 .4.4-3L10 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              )}
            </svg>
          </button>
          
          <h2>Notes</h2>
          {editingSection === 'notes' ? (
            <>
              <div className="info-grid">
                <div className="info-item full-width">
                  <label>Additional Notes</label>
                  <textarea name="notes" value={formData.notes || ''} onChange={handleInputChange} rows="5" placeholder="Add any notes about this tutee..." />
                </div>
              </div>
              <div className="section-actions">
                <button className="btn-save-section" onClick={() => saveSection('Notes')}>Save</button>
              </div>
            </>
          ) : (
            <div className="info-grid">
              <div className="info-item full-width">
                <label>Additional Notes</label>
                <p className="bio-text">{tutee.notes || 'No notes available'}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Rate Change Confirmation Modal */}
      <RateChangeConfirmModal
        isOpen={showRateChangeModal}
        onClose={() => {
          setShowRateChangeModal(false);
          setRateChangeData(null);
          setUnendorsedLessons([]);
          setPendingSaveData(null);
        }}
        onConfirm={handleRateChangeConfirm}
        unendorsedLessons={unendorsedLessons}
        oldRate={rateChangeData?.oldRate}
        newRate={rateChangeData?.newRate}
        tutorName={rateChangeData?.tutorName}
        subject={rateChangeData?.subject}
        rateType={rateChangeData?.type}
        hasMoreChanges={pendingSaveData?.remainingRateChanges?.length > 0}
      />
    </div>
  );
};

// SubjectCard Component
const SubjectCard = ({ 
  subject, 
  index, 
  isEditing, 
  availableTutors, 
  onEdit, 
  onSave, 
  onDelete, 
  onAssignTutor, 
  onRemoveTutor, 
  onCancel,
  tuteeLevel // Add tutee level for default rate calculation
}) => {
  const [editData, setEditData] = useState(subject);
  const [showTutorSelector, setShowTutorSelector] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState('');
  const [tutorRate, setTutorRate] = useState('');
  const [defaultRates, setDefaultRates] = useState({ physical: 40, online: 35 });
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    setEditData(subject);
    // Fetch default rates for this tutee level
    fetchDefaultRates();
  }, [subject, tuteeLevel]);

  const fetchDefaultRates = async () => {
    try {
      const response = await adminApi.getTutorRateSettings();
      if (response.data && response.data.rates && tuteeLevel) {
        const levelRates = response.data.rates[tuteeLevel];
        if (levelRates) {
          setDefaultRates(levelRates);
        }
      }
    } catch (err) {
      console.error('Failed to fetch default rates:', err);
    }
  };

  const handleSave = () => {
    // Convert string values to numbers before saving
    const dataToSave = { ...editData };
    
    if (dataToSave.rate) {
      dataToSave.rate = parseFloat(dataToSave.rate);
    }
    
    if (dataToSave.frequencyPerMonth) {
      dataToSave.frequencyPerMonth = parseFloat(dataToSave.frequencyPerMonth);
    }
    
    if (dataToSave.sessionDuration) {
      dataToSave.sessionDuration = parseFloat(dataToSave.sessionDuration);
    }
    
    onSave(dataToSave);
  };

  // Validation for save button
  const isValidSubject = () => {
    const isValid = editData.name && editData.name.trim() !== '' && 
           editData.rate && parseFloat(editData.rate) > 0;
    return isValid;
  };

  const handleAssignTutor = () => {
    if (selectedTutor) {
      const finalRate = tutorRate || getDefaultTutorRate();
      onAssignTutor(selectedTutor, finalRate);
      setSelectedTutor('');
      setTutorRate('');
      setShowTutorSelector(false);
    }
  };

  const getDefaultTutorRate = () => {
    const mode = (editData.mode || 'physical').toLowerCase();
    return defaultRates[mode] || defaultRates.physical || 40;
  };

  const calculateCommission = (tutorRate) => {
    const rate = parseFloat(editData.rate) || 0;
    const tRate = parseFloat(tutorRate) || 0;
    return rate - tRate;
  };

  if (isEditing) {
    return (
      <div className="subject-card-modern editing">
        <button 
          className="btn-edit-corner"
          onClick={onCancel}
          title="Cancel Edit"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        {/* In-Place Editing - Same Layout as Display Mode */}
        <div className="subject-title-section-redesigned editing">
          <input
            type="text"
            className="subject-name-input-redesigned"
            value={editData.name}
            onChange={(e) => setEditData({...editData, name: e.target.value})}
            placeholder="Subject Name"
          />
          <div className="subject-badges-row-redesigned editing">
            <select
              className={`mode-select-redesigned ${(editData.mode || 'Physical').toLowerCase()}`}
              value={editData.mode || 'Physical'}
              onChange={(e) => setEditData({...editData, mode: e.target.value})}
            >
              <option value="Physical">Physical</option>
              <option value="Online">Online</option>
            </select>
            <label className="status-toggle-redesigned">
              <input
                type="checkbox"
                checked={editData.isActive !== false}
                onChange={(e) => setEditData({...editData, isActive: e.target.checked})}
              />
              <span className={`status-badge-redesigned ${editData.isActive !== false ? 'active' : 'inactive'}`}>
                {editData.isActive !== false ? 'Active' : 'Inactive'}
              </span>
            </label>
          </div>
        </div>

        {/* Rate Editing - In-Place */}
        <div className="subject-rate-section-redesigned editing">
          <span className="rate-label-small">Tutee Rate:</span>
          <div className="rate-input-inline-redesigned">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              className="rate-input-small"
              value={editData.rate}
              onChange={(e) => setEditData({...editData, rate: e.target.value})}
              placeholder="0"
            />
            <span className="rate-unit">/hr</span>
          </div>
        </div>
        
        {/* Frequency and Duration Editing */}
        <div className="subject-schedule-section-redesigned editing">
          <div className="schedule-field-inline">
            <span className="schedule-label-small">Frequency/Month:</span>
            <input
              type="number"
              className="schedule-input-small"
              value={editData.frequencyPerMonth || ''}
              onChange={(e) => {
                const value = e.target.value;
                setEditData({...editData, frequencyPerMonth: value});
              }}
              placeholder=""
              min="0"
              step="any"
            />
            <span className="schedule-unit">sessions</span>
          </div>
          <div className="schedule-field-inline">
            <span className="schedule-label-small">Duration:</span>
            <input
              type="number"
              className="schedule-input-small"
              value={editData.sessionDuration || ''}
              onChange={(e) => {
                const value = e.target.value;
                setEditData({...editData, sessionDuration: value});
              }}
              placeholder=""
              min="0"
              step="any"
            />
            <span className="schedule-unit">hrs</span>
          </div>
        </div>
        
        <div className="subject-divider"></div>

          {/* Tutor Assignment Section */}
          <div className="tutor-assignment-section">
            <label>Assigned Tutors</label>
            <div className="assigned-tutors-grid">
              {editData.assignedTutors && editData.assignedTutors.length > 0 ? (
                editData.assignedTutors.map((assignment, tutorIdx) => {
                  // Robust tutor name resolution
                  let tutorName = 'Unknown Tutor';
                  

                  
                  // Try multiple resolution strategies
                  if (assignment.tutor?.name) {
                    tutorName = assignment.tutor.name;
                  } else if (assignment.name && assignment.name !== 'Unknown Tutor') {
                    tutorName = assignment.name;
                  } else if (assignment.tutorId && availableTutors && availableTutors.length > 0) {
                    // Try exact match first
                    let foundTutor = availableTutors.find(t => t._id === assignment.tutorId);
                    
                    // Try string conversion match
                    if (!foundTutor) {
                      foundTutor = availableTutors.find(t => String(t._id) === String(assignment.tutorId));
                    }
                    
                    // Try alternative id field
                    if (!foundTutor) {
                      foundTutor = availableTutors.find(t => t.id === assignment.tutorId);
                    }
                    
                    // Try loose matching as last resort
                    if (!foundTutor) {
                      foundTutor = availableTutors.find(t => 
                        t._id?.toString().includes(assignment.tutorId?.toString()) ||
                        assignment.tutorId?.toString().includes(t._id?.toString())
                      );
                    }
                    
                    if (foundTutor) {
                      tutorName = foundTutor.name;
                      // Update assignment for future renders
                      assignment.name = foundTutor.name;
                      assignment.tutor = foundTutor;
                    }
                  }
                  
                  // Calculate rates based on current mode (reactive to mode changes)
                  const currentMode = (editData.mode || 'physical').toLowerCase();
                  const defaultRate = defaultRates[currentMode] || defaultRates.physical || 40;
                  const hasCustomRate = assignment.tutorRate && 
                                       assignment.tutorRate !== '' && 
                                       assignment.tutorRate !== null && 
                                       assignment.tutorRate !== undefined;
                  const isUsingDefault = !hasCustomRate;
                  const finalRate = hasCustomRate ? parseFloat(assignment.tutorRate) : defaultRate;
                  const commission = parseFloat(editData.rate || 0) - finalRate;
                  
                  return (
                    <div key={tutorIdx} className="tutor-assignment-card-redesigned">
                      {/* Top Row: Tutor Header + Rate/Commission */}
                      <div className="tutor-top-row-redesigned">
                        {/* Tutor Header with Enhanced Design */}
                        <div className="tutor-header-redesigned">
                          <div className="tutor-avatar-redesigned">
                            {tutorName.charAt(0).toUpperCase()}
                          </div>
                          <div className="tutor-info-redesigned">
                            <div className="tutor-name-redesigned">{tutorName}</div>
                            {isUsingDefault && (
                              <div className="default-indicator-redesigned">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1"/>
                                  <path d="M6 3v3M6 8h.01" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                                </svg>
                                Using default rate
                              </div>
                            )}
                          </div>
                          <button 
                            className="btn-remove-tutor-redesigned"
                            onClick={() => onRemoveTutor(tutorIdx)}
                            title="Remove Tutor"
                          >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M10.5 3.5L3.5 10.5M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </div>

                        {/* Rate and Commission Grid */}
                        <div className="rate-commission-grid-redesigned">
                          {/* Rate Input Section */}
                          <div className="rate-section-redesigned">
                            <label className="rate-label-redesigned">Tutor Rate</label>
                            <div className="rate-input-wrapper-redesigned">
                              <span className="currency-symbol-redesigned">$</span>
                              <input
                                type="number"
                                className={`rate-input-redesigned ${isUsingDefault ? 'using-default' : ''}`}
                                value={hasCustomRate ? assignment.tutorRate : ''}
                                placeholder={`${defaultRate}`}
                                title={isUsingDefault ? `Leave empty to use default rate: $${defaultRate}/hr (${currentMode})` : ''}
                                key={`${tutorIdx}-${currentMode}-${defaultRate}`}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const updatedAssignments = [...editData.assignedTutors];
                                  updatedAssignments[tutorIdx] = {
                                    ...updatedAssignments[tutorIdx],
                                    tutorRate: value === '' ? null : value
                                  };
                                  setEditData({...editData, assignedTutors: updatedAssignments});
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Backspace' || e.key === 'Delete') {
                                    if (e.target.value.length <= 1) {
                                      e.preventDefault();
                                      const updatedAssignments = [...editData.assignedTutors];
                                      updatedAssignments[tutorIdx] = {
                                        ...updatedAssignments[tutorIdx],
                                        tutorRate: null
                                      };
                                      setEditData({...editData, assignedTutors: updatedAssignments});
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = e.target.value.trim();
                                  if (value === '' || value === '0') {
                                    const updatedAssignments = [...editData.assignedTutors];
                                    updatedAssignments[tutorIdx] = {
                                      ...updatedAssignments[tutorIdx],
                                      tutorRate: null
                                    };
                                    setEditData({...editData, assignedTutors: updatedAssignments});
                                  }
                                }}
                              />
                              <span className="rate-unit-redesigned">/hr</span>
                            </div>
                          </div>

                          {/* Commission Section */}
                          <div className="commission-section-redesigned">
                            <label className="commission-label-redesigned">Commission</label>
                            <div className={`commission-display-redesigned ${commission >= 0 ? 'positive' : 'negative'}`}>
                              <span className="commission-sign">{commission >= 0 ? '+' : '-'}</span>
                              <span className="commission-value">${Math.abs(commission).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Schedule Section */}
                      <div className="schedule-section-compact">
                        <label className="schedule-label-compact">Schedule</label>
                        <div className="schedules-list-compact">
                          {assignment.schedules && assignment.schedules.length > 0 ? (
                            assignment.schedules.map((schedule, schedIdx) => (
                              <div key={schedIdx} className="schedule-chip-compact">
                                <select
                                  className="schedule-day-select"
                                  value={schedule.day || ''}
                                  onChange={(e) => {
                                    const updatedAssignments = [...editData.assignedTutors];
                                    const updatedSchedules = [...(updatedAssignments[tutorIdx].schedules || [])];
                                    updatedSchedules[schedIdx] = { ...updatedSchedules[schedIdx], day: e.target.value };
                                    updatedAssignments[tutorIdx] = {
                                      ...updatedAssignments[tutorIdx],
                                      schedules: updatedSchedules
                                    };
                                    setEditData({...editData, assignedTutors: updatedAssignments});
                                  }}
                                >
                                  <option value="">Day</option>
                                  <option value="Monday">Mon</option>
                                  <option value="Tuesday">Tue</option>
                                  <option value="Wednesday">Wed</option>
                                  <option value="Thursday">Thu</option>
                                  <option value="Friday">Fri</option>
                                  <option value="Saturday">Sat</option>
                                  <option value="Sunday">Sun</option>
                                </select>
                                <input
                                  type="time"
                                  className="schedule-time-input"
                                  value={schedule.startTime || ''}
                                  onChange={(e) => {
                                    const updatedAssignments = [...editData.assignedTutors];
                                    const updatedSchedules = [...(updatedAssignments[tutorIdx].schedules || [])];
                                    updatedSchedules[schedIdx] = { ...updatedSchedules[schedIdx], startTime: e.target.value };
                                    updatedAssignments[tutorIdx] = {
                                      ...updatedAssignments[tutorIdx],
                                      schedules: updatedSchedules
                                    };
                                    setEditData({...editData, assignedTutors: updatedAssignments});
                                  }}
                                />
                                <button
                                  className="btn-remove-schedule-compact"
                                  onClick={() => {
                                    const updatedAssignments = [...editData.assignedTutors];
                                    const updatedSchedules = (updatedAssignments[tutorIdx].schedules || []).filter((_, i) => i !== schedIdx);
                                    updatedAssignments[tutorIdx] = {
                                      ...updatedAssignments[tutorIdx],
                                      schedules: updatedSchedules
                                    };
                                    setEditData({...editData, assignedTutors: updatedAssignments});
                                  }}
                                  title="Remove schedule"
                                >
                                  ×
                                </button>
                              </div>
                            ))
                          ) : (
                            <span className="no-schedule-text">No schedule set</span>
                          )}
                        </div>
                        <button
                          className="btn-add-schedule-compact"
                          onClick={() => {
                            const updatedAssignments = [...editData.assignedTutors];
                            const currentSchedules = updatedAssignments[tutorIdx].schedules || [];
                            updatedAssignments[tutorIdx] = {
                              ...updatedAssignments[tutorIdx],
                              schedules: [...currentSchedules, { day: '', startTime: '', frequency: 'weekly' }]
                            };
                            setEditData({...editData, assignedTutors: updatedAssignments});
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          Add Schedule
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-tutors-modern">No tutors assigned</div>
              )}
            </div>

            {/* Add Tutor Section */}
            {showTutorSelector ? (
              <div className="tutor-selector-section">
                <div className="form-row-modern">
                  <div className="form-group-modern">
                    <label>Select Tutor</label>
                    <select
                      value={selectedTutor}
                      onChange={(e) => setSelectedTutor(e.target.value)}
                      className="tutor-selector-modern"
                    >
                      <option value="">Choose a tutor...</option>
                      {availableTutors.map(tutor => (
                        <option key={tutor._id} value={tutor._id}>
                          {tutor.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group-modern">
                    <label>Tutor Rate ($/hr)</label>
                    <div className="input-with-tooltip">
                      <input
                        type="number"
                        value={tutorRate}
                        onChange={(e) => setTutorRate(e.target.value)}
                        placeholder={`Default: $${getDefaultTutorRate()}`}
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                        onFocus={() => setShowTooltip(true)}
                        onBlur={() => setShowTooltip(false)}
                      />
                      {showTooltip && (
                        <div className="custom-tooltip">
                          Leave empty to use default rate: $${getDefaultTutorRate()}/hr ({(editData.mode || 'physical').toLowerCase()})
                        </div>
                      )}
                    </div>

                  </div>
                </div>
                <div className="tutor-selector-actions">
                  <button 
                    className="btn-cancel-section"
                    onClick={() => {
                      setShowTutorSelector(false);
                      setSelectedTutor('');
                      setTutorRate('');
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn-save-modern"
                    onClick={handleAssignTutor}
                    disabled={!selectedTutor}
                  >
                    Assign Tutor
                  </button>
                </div>
              </div>
            ) : (
              <button 
                className="btn-add-tutor-redesigned"
                onClick={() => setShowTutorSelector(true)}
              >
                <div className="add-tutor-icon-redesigned">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 1v14M1 8h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="add-tutor-content-redesigned">
                  <span className="add-tutor-title">Add Tutor</span>
                  <span className="add-tutor-subtitle">Assign a new tutor to this subject</span>
                </div>
              </button>
            )}
          </div>

        <div className="subject-edit-actions-modern">
          <button 
            className="btn-delete-subject-modern"
            onClick={onDelete}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 4h12M5.5 4V3a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 10.5 3v1m3 0v9a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2V4h12zM6.5 7.5v4M9.5 7.5v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Delete Subject
          </button>
          <div className="edit-actions-right">
            <button 
              className="btn-cancel-section"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button 
              className="btn-save-modern"
              onClick={handleSave}
              disabled={!isValidSubject()}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subject-card-modern">
      <button 
        className="btn-edit-corner"
        onClick={onEdit}
        title="Edit Subject"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M10 1.5a1.5 1.5 0 0 1 2.1 2.1L4.5 11.1l-3 .4.4-3L10 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Subject Title Section - Prominent Display */}
      <div className="subject-title-section-redesigned">
        <h3 className="subject-name-redesigned">{subject.name}</h3>
        <div className="subject-badges-row-redesigned">
          <span className={`mode-badge-redesigned ${(subject.mode || 'physical').toLowerCase()}`}>
            {(subject.mode || 'physical').charAt(0).toUpperCase() + (subject.mode || 'physical').slice(1)}
          </span>
          <span className={`status-badge-redesigned ${subject.isActive !== false ? 'active' : 'inactive'}`}>
            {subject.isActive !== false ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Rate Display - Secondary Information */}
      <div className="subject-rate-section-redesigned">
        <span className="rate-label-small">Tutee Rate:</span>
        <span className="rate-value-small">${subject.rate}/hr</span>
      </div>
      
      {/* Frequency and Duration Display */}
      {(Number(subject.frequencyPerMonth) > 0 || Number(subject.sessionDuration) > 0) && (
        <div className="subject-schedule-section-redesigned">
          {Number(subject.frequencyPerMonth) > 0 && (
            <div className="schedule-info-item">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: '4px' }}>
                <rect x="1" y="2" width="12" height="11" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M1 5h12M4 0v4M10 0v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              <span className="schedule-label-small">Frequency:</span>
              <span className="schedule-value-small">{subject.frequencyPerMonth} sessions/month</span>
            </div>
          )}
          {Number(subject.sessionDuration) > 0 && (
            <div className="schedule-info-item">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: '4px' }}>
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M7 3v4l2.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              <span className="schedule-label-small">Duration:</span>
              <span className="schedule-value-small">{subject.sessionDuration} hrs/session</span>
            </div>
          )}
        </div>
      )}
      
      <div className="subject-divider"></div>
      
      {/* Tutor Assignment Section */}
      <div className="tutor-assignment-modern">
        <div className="tutor-section-header">
          <label className="tutor-label">Assigned Tutors</label>
        </div>
        
        {subject.assignedTutors && subject.assignedTutors.length > 0 ? (
          <div className="assigned-tutors-grid">
            {subject.assignedTutors.map((assignment, tutorIdx) => {
              // Robust tutor name resolution for display mode
              let tutorName = 'Unknown Tutor';
              
              // Try multiple resolution strategies
              if (assignment.tutor?.name) {
                tutorName = assignment.tutor.name;
              } else if (assignment.name && assignment.name !== 'Unknown Tutor') {
                tutorName = assignment.name;
              } else if (assignment.tutorId && availableTutors && availableTutors.length > 0) {
                // Try exact match first
                let foundTutor = availableTutors.find(t => t._id === assignment.tutorId);
                
                // Try string conversion match
                if (!foundTutor) {
                  foundTutor = availableTutors.find(t => String(t._id) === String(assignment.tutorId));
                }
                
                // Try alternative id field
                if (!foundTutor) {
                  foundTutor = availableTutors.find(t => t.id === assignment.tutorId);
                }
                
                // Try loose matching as last resort
                if (!foundTutor) {
                  foundTutor = availableTutors.find(t => 
                    t._id?.toString().includes(assignment.tutorId?.toString()) ||
                    assignment.tutorId?.toString().includes(t._id?.toString())
                  );
                }
                
                if (foundTutor) {
                  tutorName = foundTutor.name;
                }
              }
              
              // Calculate rates based on current mode
              const currentMode = (subject.mode || 'physical').toLowerCase();
              const defaultRate = defaultRates[currentMode] || defaultRates.physical || 40;
              const hasCustomRate = assignment.tutorRate && 
                                   assignment.tutorRate !== '' && 
                                   assignment.tutorRate !== null && 
                                   assignment.tutorRate !== undefined;
              const isUsingDefault = !hasCustomRate;
              const finalRate = hasCustomRate ? assignment.tutorRate : defaultRate;
              const commission = calculateCommission(finalRate);
              
              return (
                <div key={tutorIdx} className="tutor-assignment-card-display-redesigned">
                  {/* Top Row: Tutor Header + Rate/Commission */}
                  <div className="tutor-top-row-display-redesigned">
                    {/* Tutor Header with Enhanced Design */}
                    <div className="tutor-header-display-redesigned">
                      <div className="tutor-avatar-display-redesigned">
                        {tutorName.charAt(0).toUpperCase()}
                      </div>
                      <div className="tutor-info-display-redesigned">
                        <div className="tutor-name-display-redesigned">{tutorName}</div>
                        {isUsingDefault && (
                          <div className="default-indicator-display-redesigned">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1"/>
                              <path d="M6 3v3M6 8h.01" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                            </svg>
                            Using default rate
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Rate and Commission Display */}
                    <div className="rate-commission-display-redesigned">
                      <div className="rate-display-section-redesigned">
                        <label className="rate-label-display-redesigned">Rate</label>
                        <div className="rate-value-display-redesigned">
                          <span className="currency-symbol-display-redesigned">$</span>
                          <span className={`rate-amount-display-redesigned ${isUsingDefault ? 'default-rate' : ''}`}>
                            {finalRate}
                          </span>
                          <span className="rate-unit-display-redesigned">/hr</span>
                          {isUsingDefault && <span className="default-badge-display-redesigned">Default</span>}
                        </div>
                      </div>

                      <div className="commission-display-section-redesigned">
                        <label className="commission-label-display-redesigned">Commission</label>
                        <div className={`commission-value-display-redesigned ${commission >= 0 ? 'positive' : 'negative'}`}>
                          <span className="commission-sign-display">{commission >= 0 ? '+' : '-'}</span>
                          <span className="commission-amount-display">${Math.abs(commission).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Schedule Display */}
                  {(() => {
                    console.log('Assignment schedules:', assignment.schedules);
                    return assignment.schedules && assignment.schedules.length > 0 && (
                      <div className="schedule-display-compact">
                        <label className="schedule-label-display">Schedule</label>
                        <div className="schedules-display-list">
                          {assignment.schedules.map((schedule, schedIdx) => {
                            console.log('Schedule item:', schedule);
                            return schedule.day && schedule.startTime && (
                              <div key={schedIdx} className="schedule-badge-compact">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <rect x="1" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1"/>
                                  <path d="M1 4h10M3.5 1v2M8.5 1v2" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                                </svg>
                                <span className="schedule-day-display">{schedule.day.substring(0, 3)}</span>
                                <span className="schedule-time-display">{schedule.startTime}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-tutors-modern">
            No tutors assigned yet
          </div>
        )}
      </div>
    </div>
  );
};

// DefaultRatesDisplay Component
const DefaultRatesDisplay = ({ tuteeLevel }) => {
  const [defaultRates, setDefaultRates] = useState({ physical: 40, online: 35 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDefaultRates();
  }, [tuteeLevel]);

  const fetchDefaultRates = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getTutorRateSettings();
      if (response.data && response.data.rates && tuteeLevel) {
        const levelRates = response.data.rates[tuteeLevel];
        if (levelRates) {
          setDefaultRates(levelRates);
        }
      }
    } catch (err) {
      console.error('Failed to fetch default rates:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="default-rates-display loading">
        <span className="default-rates-label">Loading default rates...</span>
      </div>
    );
  }

  return (
    <div className="default-rates-display">
      <span className="default-rates-label">Default Tutor Rates for {tuteeLevel}:</span>
      <div className="default-rate-item">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M6 1L2 3v6l4 2 4-2V3L6 1z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Physical: ${defaultRates.physical}/hr
      </div>
      <div className="default-rate-item">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M1 3h10M1 7h10M1 11h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        Online: ${defaultRates.online}/hr
      </div>
    </div>
  );
};

export default TuteeProfile;